import {Injectable, signal} from '@angular/core';
import {AnyProcess, ProcessService} from './process.service';

type ProcessPayload = AnyProcess;

/**
 * Maps stable button IDs to process state. Buttons show a spinner while
 * their process runs. State persists in memory across navigation (the service
 * is providedIn: root).
 *
 * Usage:
 *   // Immediate spinner on click:
 *   pb.setPending('start-' + id);
 *
 *   // When mutation returns a process:
 *   pb.bind('start-' + id, process);
 *   pb.bind('start-' + id, process, (state) => { ... });
 *
 *   // For non-process actions (stop):
 *   pb.setPending('stop-' + id);
 *   // ... later, from poll or callback:
 *   pb.clear('stop-' + id);
 *
 *   // In template via directive:
 *   <a [processButton]="'start-' + host.instanceId" ...>Start</a>
 */
@Injectable({providedIn: 'root'})
export class ProcessButtonService {
  /** Set of active button IDs (pending or process-bound). */
  private active = new Set<string>();

  /** buttonId -> process id it is already bound to, so re-binding the same
   *  pair (every poll re-adopts running processes) adds no subscription. */
  private bound = new Map<string, string>();

  /** Signal that increments on every change, so directives react. */
  readonly version = signal(0);

  constructor(private processService: ProcessService) {}

  /**
   * Immediately mark a button as active (spinner). Call this on click,
   * before the mutation response arrives.
   */
  setPending(buttonId: string) {
    this.active.add(buttonId);
    this.version.update(v => v + 1);
  }

  /**
   * Bind a button to a process. The spinner stays until the process
   * reaches SUCCESS or FAILED. Optionally calls onComplete with the
   * final state.
   */
  bind(buttonId: string, process: ProcessPayload, onComplete?: (state: string, process: ProcessPayload) => void) {
    this.active.add(buttonId);
    this.version.update(v => v + 1);

    if (this.bound.get(buttonId) === process.id) {
      return;  // already watching this exact process for this button
    }
    this.bound.set(buttonId, process.id);

    this.processService.observeProcess(process).subscribe({
      next: (p) => {
        if (p.state === 'SUCCESS' || p.state === 'FAILED') {
          this.clear(buttonId);
          onComplete?.(p.state, p);
        }
      },
      complete: () => {
        this.clear(buttonId);
        onComplete?.('SUCCESS', process);
      },
    });
  }

  /** Clear a button's active state (remove spinner). */
  clear(buttonId: string) {
    this.bound.delete(buttonId);
    if (this.active.delete(buttonId)) {
      this.version.update(v => v + 1);
    }
  }

  /** Check if a button is currently active. */
  isActive(buttonId: string): boolean {
    return this.active.has(buttonId);
  }
}
